import React, { useEffect, useState } from 'react';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';

import CartImage from './Image';
import '../styles/Image.css';
import '../styles/cartSections.css';
//import useCart from '../hooks/useCart';
import { getSessionId } from '../utils/sessionId';
import { getSelectedDineInTableId, getSelectedTableIds } from '../utils/tableSelection';
import { useNavigate, useLocation } from 'react-router-dom';
import  CartCommentModal  from './modals/CartCommentModal';
import QuantityKeypadModal from './modals/QuantityKeypadModal';
import CustomItemModal from './modals/CustomItemModal';
import SelectServerModal from './modals/SelectServerModal';
import { HTTP_STATUS } from '../utils/httpStatusCodes';
import { toast } from 'react-toastify';
import { getActiveServerId } from '../utils/staffSession';
import {
  isOfflineAndroid,
  saveOfflineOrder,
  getOfflineServers
} from '../utils/androidOfflineCart';

const TakeawayCart = ({ cartData, refresh, onCartChange,  customItemProduct, onEditCartItem, LoadDishesByDeals, editOrderId, tableName, tabId, isPagerOrder, onDealChildDelete, onDealAddItem, onExistingOrderResolved }) => {
  const {
    cartItems,
    netTotal,
    dealsItem,
    halfAndHalfItems,
    removeID,
    tooltipVisible,
    tooltipText,
    setTooltipVisible,
    setTooltipText,
    handleUpdateQty,
    handleRemoveItem,
    fetchCartWithDeals,
    servers
  } = cartData;

  // cartData is fetched by table (displayOrderId in Takeaway.js), not by this component's
  // editOrderId prop, so it already carries real is_previously_added flags for every item -
  // a brand new cart just has none of them true. Order Review/Send to Kitchen only make sense
  // if at least one item hasn't already been sent to the kitchen.
  const hasNewItems = cartItems.some((item) => !item.is_previously_added)
    || dealsItem.some((deal) => !deal.is_previously_added)
    || halfAndHalfItems.some((item) => !item.is_previously_added);


  /**
   * Removing a cart line is only PIN-gated once the item has actually been fired to the kitchen.
   *
   * Until then the cart is a draft: a change of mind, or a mis-tap while taking the order. Asking
   * for a manager there blocks ordinary service — and for most restaurants it blocks it outright,
   * because `order.delete_item` is granted to Admin/Manager only and the majority have no staff
   * PINs configured at all, so the prompt can never be satisfied and the item simply won't delete.
   *
   * `is_previously_added` (backed by cart.sent_to_kitchen_at) marks an item already on a kitchen
   * docket, which is what splits the cart into the two sections and turns its quantity stepper into
   * a Repeat button.
   *
   * Removing a fired item is a real void and ought to need a manager PIN, but that gate depends on
   * the staff-permissions work and is deliberately not part of this change - removal here keeps the
   * behaviour already on master. `wasSentToKitchen` is threaded through every call site so adding
   * the gate later is a change in one function, not a hunt through the file.
   *
   * Attribution is kept regardless: a delete is recorded against whoever is signed in rather than
   * becoming anonymous.
   */
  const activeStaffId = () => Number(localStorage.getItem('active_staff_id')) || null;

  // eslint-disable-next-line no-unused-vars
  const handleRemoveCartItem = async (cartItem, wasSentToKitchen = false) => {
    handleRemoveItem(cartItem, activeStaffId());
  };

  // Delete a specific deal child, then reopen popup with remaining items pre-selected.
  const handleDealChildRemove = async (nestedDeal, deal_record) => {
    // A child counts as fired if either it or its parent deal has been sent.
    const success = await handleRemoveItem(nestedDeal, activeStaffId());
    if (success && onDealChildDelete) {
      const children = deal_record[deal_record.cid] || [];
      const remaining = children
        .filter(c => c.cart_id !== nestedDeal.cart_id)
        .map(c => c.product_id);
      onDealChildDelete(deal_record.item_id, remaining, nestedDeal.product_id, deal_record.cart_id);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  //const { orderType } = location.state || localStorage.getItem('orderType') || null; // Access the passed state
  //const isTakeaway = orderType === 'takeaway'?true:false;
  const orderType = location.state?.orderType || localStorage.getItem('orderType'); 
  const isTakeaway = orderType === 'takeaway';

  const [isLoading, setIsLoading] = useState(false);

  const [existingOrderId, setExistingOrderId] = useState(null);
  const [loadingExistingOrder, setLoadingExistingOrder] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [activeCartId, setActiveCartId] = useState(null);
  const [isCustomItemModalOpen, setIsCustomItemModalOpen] = useState(false);
  const [isSendingToKitchen, setIsSendingToKitchen] = useState(false);
  const [isPagerCheckingOut, setIsPagerCheckingOut] = useState(false);
  const [isServerModalOpen, setIsServerModalOpen] = useState(false);
  const [modalServers, setModalServers] = useState(servers || []);
  // Which submit SelectServerModal's onConfirm should resume once a server is picked - "Send to
  // Kitchen" and pager "Quick Checkout" share that one modal/prompt, so this remembers which of
  // the two triggered it.
  const [serverModalIntent, setServerModalIntent] = useState('kitchen');
  // isPagerOrder (from Takeaway.js, derived off the tabId every entry point in
  // useTableOrdering.js now passes through) - this cart's own "Send to Kitchen" shortcut
  // (submitSendToKitchen below) submits service_type directly, bypassing Order Review entirely,
  // and its "Order Review" button forwards this state on as that page's initialServiceType, so
  // both must already default/offer the Takeaway/Dine-in pair rather than the 4 dine-in options.
  const [serviceType, setServiceType] = useState(isPagerOrder ? 'pager_takeaway' : 'entree_first');
  const [repeatingCartId, setRepeatingCartId] = useState(null);
  const [quantityEditItem, setQuantityEditItem] = useState(null);

  // Repeating an already-fired item must never edit its own quantity (the kitchen already has
  // that ticket) - it inserts a brand new pending cart row instead, via the same
  // repeat-cart-items endpoint the multi-item "repeat previous order" picker
  // (FinalizeOrderCartItemModal.js) already uses, just with a single cart id.
  const handleRepeatItem = async (item) => {
    setRepeatingCartId(item.cart_id);
    try {
      const payload = new FormData();
      payload.append('cart_ids', JSON.stringify([item.cart_id]));
      payload.append('session_id', getSessionId());
      const tableId = getSelectedDineInTableId();
      if (tableId) {
        payload.append('table_id', tableId);
      }
      const response = await callAPI(apiUrls.REPEAT_ITEMS, {}, 'POST', payload, {});
      if (response?.data?.status === 1) {
        toast.success('Item repeated');
        onCartChange?.();
      } else {
        toast.error(response?.data?.message || 'Failed to repeat item');
      }
    } catch (error) {
      console.error('Error repeating item:', error);
      toast.error('Failed to repeat item');
    } finally {
      setRepeatingCartId(null);
    }
  };

  
  useEffect(() => {
    if (orderType !== 'dinein') {
      return;
    }
    const raw = localStorage.getItem('selectedTables');
        
    if (!raw) return;
  
    let selectedTables = [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
      
    if (Array.isArray(parsed)) {
      selectedTables = parsed;
    }  else if (parsed && typeof parsed === 'object') {
      selectedTables = Object.values(parsed).flat();
    }

    console.log("normalized tables:", selectedTables);

    if (!selectedTables.length) return;
 
     const tableId = selectedTables[0];
    
  
    
    if (!tableId || isNaN(Number(tableId))) {
      console.warn('Skipping existing order fetch: invalid tableId', tableId);
      return;
    }
  
    const fetchExistingOrder = async () => {
      try {
        setLoadingExistingOrder(true);
  
        const response = await callAPI(
          `${apiUrls.GET_EXISTING_ORDER_TABLE}?existing_table_id=${tableId}`,
          {},
          'GET'
        );
  
        const data = response?.data;
  
        if (data?.status === 1 && data?.data?.existing_order_id) {
          console.log("exisitinggg",data.data.existing_order_id)
          setExistingOrderId(data.data.existing_order_id);
          onExistingOrderResolved?.(data.data.existing_order_id, tableId);
        } else {
          setExistingOrderId(null);
        }
      } catch (err) {
        setExistingOrderId(null);
      } finally {
        setLoadingExistingOrder(false);
      }
    };

    fetchExistingOrder();
  }, []);
  

  const handleQuickCheckout = async (e) => {
    e?.preventDefault?.();
    setIsLoading(true); // Show loader
    try {
      if (isOfflineAndroid()) {
      const sessionId = getSessionId();
      const serverId = getActiveServerId() || null;

      console.log(
        '[OFFLINE_QUICK_CHECKOUT] Creating takeaway order',
        JSON.stringify({
          sessionId,
          orderType: 'takeaway',
          tableId: null,
          existingOrderId: null,
          serverId,
          serviceType: 'takeaway',
        })
      );

      const result = await saveOfflineOrder({
        sessionId,
        orderType: 'takeaway',
        tableId: null,
        existingOrderId: null,
        serverId,
        serviceType: 'takeaway',
      });

      console.log(
        '[OFFLINE_QUICK_CHECKOUT] Result:',
        JSON.stringify(result, null, 2)
      );

      if (!result?.success) {
        toast.error(
          result?.error || 'Failed to save offline order'
        );
        return;
      }

      // Use the locally generated order ID.
      const offlineOrderId = result?.localOrderId;

      if (!offlineOrderId) {
        console.error(
          '[OFFLINE_QUICK_CHECKOUT] Missing local order ID',
          result
        );

        toast.error(
          'Order was saved, but its local ID could not be retrieved.'
        );
        return;
      }

      localStorage.setItem(
        'current_order_id',
        String(offlineOrderId)
      );

      localStorage.setItem(
        'order_id',
        String(offlineOrderId)
      );

      toast.success('Order created');

      navigate('/select-customer', {
        state: {
          order_id: offlineOrderId,
          offline: true,
        },
      });

      navigate('/payment-view?order_id='+offlineOrderId, {
        state: {
          orderType: 'takeaway',
          order_id: offlineOrderId,
        },
      });

      return;
    }

    const formData = new FormData();
    formData.append('express-order', true);
    formData.append('is_takeaway', isTakeaway ? '1' : '0');
    formData.append('session_id', getSessionId());
    formData.append('server_id', getActiveServerId() || '');

    
        const res = await callAPI(apiUrls.EXPRESS_CHECKOUT, {}, 'POST', formData);
        if (res.status === 200) {
            //alert('/payment-view');
            // Uncomment the line below to redirect
            //alert(data.data.order_id);
            localStorage.setItem('order_id', res.data.order_id);
            //window.location.href = '/payment-view';
            navigate('/payment-view?order_id='+res.data.data.order.id, {
              state: {
                orderType: localStorage.getItem('orderType'),
                order_id: res.data.data.order.id,
              },
            });
        }
    } catch (error) {
        console.error('Error during quick checkout:', error);
    } finally {
      setIsLoading(false); // Stop loader
    }
  };

  // Same order-create call OrderReviewSidebar's "SEND TO KITCHEN" button makes for a
  // dine-in order, triggered directly from the cart so staff don't have to leave this
  // screen just to resubmit after adding more items to an already-occupied table.
  const submitSendToKitchen = async (serverId = null) => {
    const selectedTablesForSubmit = getSelectedTableIds();
    // Neither piece of table context is available - the backend would reject this anyway
    // (OrdersController::actionOrderCreate()'s table_selection guard), just faster and without
    // wasting a round-trip. Matters most when localStorage's 'selectedTables' has gone stale/
    // missing on this device between selecting the table and sending to kitchen - that's what
    // used to silently create an order with a blank, unlinkable table_id instead of failing here.
    if (!existingOrderId && (!selectedTablesForSubmit || selectedTablesForSubmit.length === 0)) {
      toast.error('Table is not selected. Please go back and select a table.');
      return;
    }

    setIsSendingToKitchen(true);
    try {
        if (isOfflineAndroid()) {
          const tableId =
            orderType === 'dinein'
              ? getSelectedDineInTableId()
              : null;

                 console.log(
                    '[OFFLINE_ORDER] Sending to kitchen',
                    JSON.stringify({
                      sessionId: getSessionId(),
                      orderType,
                      tableId,
                      existingOrderId,
                      serverId,
                      serviceType,
                    })
                  );
          
          const result = await saveOfflineOrder({
            sessionId: getSessionId(),
            orderType,
            tableId,
            existingOrderId,
            serverId,
            serviceType,
          });

          if (result?.success) {
            toast.success('Sent to kitchen');

            setIsServerModalOpen(false);

            onCartChange?.();

            navigate('/dashboard');
          } else {
            toast.error(
              result?.error || 'Failed to save offline order'
            );
          }

          return;
     }

      const session_id = getSessionId();
      const payload = {
        server_id: serverId,
        checkout_type: 'finalise-order',
        order_type: 'dine_in',
        sub_order_type: 'take_away',
        service_type: serviceType,
        order_name: '',
        cust_mobile: '',
        cust_name: '',
        selected_tables: selectedTablesForSubmit,
        session_id,
      };
      // existing_order_id is what associates this send with the table's open order;
      // OrderService::createOrder() reads it and calls setExistingOrderId(). edit_order_id is
      // deliberately NOT sent: OrdersController branches on it into editPosOrder(), which updates
      // order name/server/service type and nothing else - no order created, no kitchen docket.
      if (existingOrderId) payload.existing_order_id = existingOrderId;

      const response = await callAPI(apiUrls.ORDER_CREATE, {}, 'POST', payload, {});

      if (response?.data?.status === 1) {
        toast.success('Sent to kitchen');
        setIsServerModalOpen(false);
        navigate('/dashboard');
      } else {
        // Same as OrderReviewSidebar::handleSendToKitchen - the backend only rejects
        // order-create outright for session/cart-state problems this screen can't fix (e.g.
        // "No items selected", "Table is Not Selected... Start Fresh!"). Staying here just
        // leaves the user stuck, so redirect home after showing why.
        toast.error(response?.data?.message || 'Failed to send to kitchen');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Quick send to kitchen error:', error);
      toast.error('Failed to send to kitchen');
    } finally {
      setIsSendingToKitchen(false);
    }
  };

  const handleQuickSendToKitchen = async () => {
    if (isSendingToKitchen) return;

    // A PIN-authenticated staff member tags the order directly — asking them to also
    // pick their own name from the server list would be redundant.
    const activeServerId = getActiveServerId();
    if (activeServerId) {
      submitSendToKitchen(activeServerId);
      return;
    }

   if (isOfflineAndroid()) {
    try {
      const response = await getOfflineServers();

      if (!response?.success) {
        toast.error(
          response?.error || 'Failed to load offline servers.'
        );
        return;
      }

      const offlineServers = Array.isArray(response.data)
        ? response.data
        : [];

      console.log(
        '[OFFLINE_SERVERS] Loaded servers:',
        offlineServers
      );

      if (offlineServers.length === 0) {
        toast.error('No servers available offline.');
        return;
      }

      // Store the Room servers for SelectServerModal.
      setModalServers(offlineServers);

      // Open the modal only after fetching the servers.
      setServerModalIntent('kitchen');
      setIsServerModalOpen(true);

      return;
    } catch (error) {
      console.error(
        '[OFFLINE_SERVERS] Failed to load servers:',
        error
      );

      toast.error('Failed to load offline servers.');
      return;
    }
  }
   const onlineServers = Array.isArray(servers) ? servers : [];
   if (onlineServers.length > 0) {
      setModalServers(onlineServers);
      setServerModalIntent('kitchen');
      setIsServerModalOpen(true);
      return;
    }

    submitSendToKitchen(null);
  };

  // A pager order is always prepaid at the counter, so its "quick" path skips straight to
  // payment instead of the kitchen - same order-create call as OrderReviewSidebar's PREPAID
  // button (checkout_type: 'makepayment'), just triggered from the cart page directly. Order
  // Review is still reachable via its own button above; this is the shortcut past it, matching
  // Takeaway's own "Quick Checkout" pattern for phone/counter orders (handleQuickCheckout above).
  const submitPagerQuickCheckout = async (serverId = null) => {
    setIsPagerCheckingOut(true);
    try {
      const session_id = getSessionId();
      const rawSelectedTables = JSON.parse(localStorage.getItem('selectedTables') || '{}');
      const tableId = Array.isArray(rawSelectedTables)
        ? rawSelectedTables[0]
        : Object.values(rawSelectedTables || {}).flat()[0];

      const payload = {
        server_id: serverId,
        checkout_type: 'makepayment',
        order_type: 'dine_in',
        sub_order_type: 'take_away',
        service_type: serviceType,
        order_name: '',
        cust_mobile: '',
        cust_name: '',
        selected_tables: rawSelectedTables,
        session_id,
      };
      if (existingOrderId) payload.existing_order_id = existingOrderId;

      const response = await callAPI(apiUrls.ORDER_CREATE, {}, 'POST', payload, {});

      if (response?.data?.status === 1) {
        const orderId = response?.data?.data?.order_id;
        if (orderId) localStorage.setItem('current_order_id', orderId);
        setIsServerModalOpen(false);
        // Customer name/profile selection is deferred to the payment page itself (its own
        // AddSelectCustomerModal), same as every other prepaid-at-checkout flow - this button
        // is specifically the shortcut that skips collecting it here.
        navigate(`/payment-view?table_id=${tableId}`, {
          state: { table_id: tableId, is_prepaid: true },
        });
      } else {
        toast.error(response?.data?.message || 'Failed to start checkout');
      }
    } catch (error) {
      console.error('Pager quick checkout error:', error);
      toast.error('Failed to start checkout');
    } finally {
      setIsPagerCheckingOut(false);
    }
  };

  const handlePagerQuickCheckout = () => {
    if (isPagerCheckingOut) return;

    const activeServerId = getActiveServerId();
    if (activeServerId) {
      submitPagerQuickCheckout(activeServerId);
      return;
    }

    const hasServers = Array.isArray(servers) && servers.length > 0;
    if (hasServers) {
      setServerModalIntent('checkout');
      setIsServerModalOpen(true);
      return;
    }

    submitPagerQuickCheckout(null);
  };

  const handleAddCustomItem = async (data) => {
    try {
      const payload = new FormData();
  
      payload.append('qty', 1);
      payload.append('type', 'updated_price');
      payload.append('default_ingredients_quantity', JSON.stringify([]));
      payload.append('add_on_ingredients_quantity', JSON.stringify([]));
  
      
      payload.append('id', customItemProduct.product_id);
  
      
      const sizeId = customItemProduct?.product_details_with_sizes?.[0]?.size_id ?? 1;
  
      payload.append('size', sizeId);
  
      payload.append('cust_product_name', data.item_name);
      payload.append('cust_product_price', data.price);
      payload.append('is_custom_item', 1);
  
      payload.append('session_id', getSessionId());

      const phone_order = orderType === 'takeaway' || orderType === 'phone' ? 1 : 0;

      payload.append('phone_order', phone_order);
      payload.append('selected_customer_id', 1);

      const addCustomItemActiveServerId = getActiveServerId();
      if (addCustomItemActiveServerId) {
        payload.append('added_by_employee_id', addCustomItemActiveServerId);
      }

      if (editOrderId) {
        payload.append('edit_order_id', editOrderId);
      }
      if (orderType === 'dinein') {
        const tableId = getSelectedDineInTableId();
        if (tableId) {
          payload.append('table_id', tableId);
        }
      }

      console.log("customItemProduct", customItemProduct);
      console.log("sizeId", sizeId);
  
      const response = await callAPI(
        apiUrls.ADD_TO_CART_DETAIL,
        {},
        'POST',
        payload,
        {}
      );
  
      if (response.status === HTTP_STATUS.OK) {
        toast.success("Custom item added to cart");
  
        onCartChange?.();
  
        setIsCustomItemModalOpen(false);
      }
  
    } catch (error) {
      console.error("Custom item error:", error);
      toast.error("Failed to add custom item");
      setIsCustomItemModalOpen(false);
    }
  };

  return (
    <div className="cartSidebar screenHeightCol">
      <div className="cartSidebarHeader">
        <h4 className="smTitle mb-0">{orderType === 'dinein' && tableName ? `${isPagerOrder ? 'Pager' : 'Table'} ${tableName}` : 'Your Cart'}</h4>
        {customItemProduct && (
          <button
              className="btn btnPrimary smBtn"
              onClick={() => setIsCustomItemModalOpen(true)}
          >
              Add custom item
          </button>
        )}
      </div>

      <div className="cartSidebarBody">
        <div className="cartCol">
          {cartItems.length === 0 && dealsItem.length === 0 && halfAndHalfItems.length === 0 ? (
            <div>No items in cart.</div>
          ) : (
            <>
            {dealsItem.map((deal_record) => (
            <div class="cartSidebarPannel dealSidebar" key={deal_record.cart_id || deal_record.id}>
              <div class="mProTopRow">
                  <CartImage src={deal_record.product_thumbnail} alt={deal_record.item_name} />
                  <div class="mProName">{deal_record.item_name}</div>
                  <span class="cartRightIcons">
                      <svg width="20" height="20" 
                        class="cartRightIcon cursorPointer"
                        onClick={() => handleRemoveCartItem(deal_record.cart_id, !!deal_record.is_previously_added)}
                        style={removeID === deal_record.cart_id ? { color: '#999', pointerEvents: 'none' } : {}}
                        >
                          <use xlinkHref="./images/sprite.svg#iconTrash"></use>
                      </svg>

                  </span>
              </div>
              <p>{deal_record.productDetails}</p>
              <p>{deal_record.highlighter}</p>
              <div class="sbRow">
                <span class="priceLg">${deal_record.total_price}</span>
              </div>
              {/* Nested Deals */}
                {deal_record[deal_record.cid] && Array.isArray(deal_record[deal_record.cid]) && deal_record[deal_record.cid].length > 0 && (
                  deal_record[deal_record.cid].map((nestedDeal) => (
                    
                    <div className="cartSidebarPannel" key={nestedDeal.cart_id}>
                      <div className="mProTopRow">
                        <CartImage src={nestedDeal.product_thumbnail} alt={nestedDeal.item_name} />
                        <div className="mProName" style={{paddingLeft:"35px"}}>{nestedDeal.item_name} - {nestedDeal.selected_size}</div>
                        <span className="priceLg">${nestedDeal.total_price}</span>
                      </div>
                      <span dangerouslySetInnerHTML={{ __html: nestedDeal.dealIngrade }} style={{ fontSize: 13 }} />
                       <span dangerouslySetInnerHTML={{ __html: nestedDeal.dealUpgrade }} style={{ fontSize: 13, marginTop: 2 }} />
                      {nestedDeal.comments && (
                        <span className="proDetail d-block fst-italic">Comment: {nestedDeal.comments}</span>
                      )}
                      {/* <p>
                        {nestedDeal.product_detail ? (
                          <span dangerouslySetInnerHTML={{ __html: nestedDeal.product_detail }} />
                        ) : (
                          <span></span>
                        )}
                      </p> */}
                      <div className="cartItemActionsRow">
                        <div className="cartItemActions">
                          <button
                            type="button"
                            className="cartRemoveBtn"
                            onClick={() => handleDealChildRemove(nestedDeal, deal_record)}
                            disabled={removeID === nestedDeal.cart_id}
                          >
                            <svg width="14" height="14">
                              <use xlinkHref="/images/sprite.svg#iconTrash"></use>
                            </svg>
                            Remove
                          </button>
                          <button
                            type="button"
                            className="cartCommentBtn"
                            onClick={() => {
                              setActiveCartId(nestedDeal.cart_id);
                              setIsCommentModalOpen(true);
                            }}
                          >
                            <svg width="14" height="14">
                              <use xlinkHref="/images/sprite.svg#iconMessage" />
                            </svg>
                            Comment
                          </button>
                        </div>
                      </div>
                    </div>

                  ))
                )}

                {/* Total Saved */}
                {deal_record.total_saved > 0 && (
                  <p>{`Total Saved: $${deal_record.total_saved}`}</p>
                )}
              {deal_record.info && (
              <div class="addItemsCol">
                  <ul>
                    {Object.entries(deal_record.info).map(([key, value]) => (
                    <li key={key}>
                      <a
                        href="/"
                        data-hosturl={value.hostUrl}
                        data-subcat={value.sub_cat_in_logic}
                        data-productid={value.product_id}
                        data-sizeid={value.size_id}
                        onClick={(e) => {
                          e.preventDefault();
                          if (onDealAddItem) {
                            const children = deal_record[deal_record.cid] || [];
                            const remaining = children.map(c => c.product_id);
                            onDealAddItem(deal_record.item_id, deal_record.cart_id, remaining);
                          } else {
                            LoadDishesByDeals(value.hostUrl, value.sub_cat_in_logic, value.product_id, value.size_id);
                          }
                        }}
                      >
                        <span>
                        <svg width="20" height="20" class="addIcon">
                            <use xlinkHref="./images/sprite.svg#iconplusIcon"></use>
                        </svg>
                        </span>
                        <span>Add Item</span>
                        <span
                          className="infoToolIcon"
                          onMouseEnter={() => {
                            console.log(value.infoText);
                            setTooltipText(value.infoText); // Set the tooltip text
                            setTooltipVisible(key); // Show the tooltip
                          }}
                          onMouseLeave={() => {
                            setTooltipVisible(false); // Hide the tooltip
                          }}
                          style={{ position: 'relative' }}
                        >
                          <img src="images/info-icon.svg" alt="..." />
                          <i
                            className="fa fa-info-circle opentooltips"
                            aria-hidden="true"
                          ></i>
                          {/* Tooltip */}
                          {tooltipVisible === key && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#333',
                                color: '#fff',
                                padding: '5px 10px 5px 25px',
                                borderRadius: '4px',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                zIndex: 1000,
                                minWidth: '300px'
                              }}
                            >
                              {tooltipText}
                            </div>
                          )}
                        </span>
                      </a>
                    </li>
                    ))}
                  </ul>
                </div>
                )}
            </div>
            ))}

            {halfAndHalfItems.map((hnh_record) => (
              <div className="cartSidebarPannel dealSidebar" key={hnh_record.cart_id || hnh_record.id}>
                <div className="mProTopRow">
                  <div className="mProName">{hnh_record.item_name}</div>
                  <span className="priceLg">${hnh_record.total_price}</span>
                </div>
                {hnh_record.crust_label && (
                  <span className="proDetail d-block fst-italic">Crust: {hnh_record.crust_label} (${hnh_record.crust_price})</span>
                )}
                <div className="cartItemActionsRow">
                  <div className="cartItemActions">
                    <button
                      type="button"
                      className="cartRemoveBtn"
                      onClick={() => handleRemoveCartItem(hnh_record.cart_id, !!hnh_record.is_previously_added)}
                      disabled={removeID === hnh_record.cart_id}
                    >
                      <svg width="14" height="14">
                        <use xlinkHref="/images/sprite.svg#iconTrash"></use>
                      </svg>
                      Remove
                    </button>
                  </div>
                </div>
                {hnh_record[hnh_record.cid] && Array.isArray(hnh_record[hnh_record.cid]) && hnh_record[hnh_record.cid].length > 0 && (
                  hnh_record[hnh_record.cid].map((half) => (
                    <div className="cartSidebarPannel cartHalfPannel" key={half.cart_id}>
                      <div className="mProTopRow">
                        <CartImage src={half.product_thumbnail} alt={half.item_name} />
                        <div className="mProName">{half.item_name} - {half.selected_size}</div>
                        <span className="priceLg">${half.total_price}</span>
                      </div>
                      {half.product_detail_text && (
                        <p>
                          <span dangerouslySetInnerHTML={{ __html: half.product_detail_text }} />
                        </p>
                      )}
                      {half.toppings && half.toppings.map((topping) => (
                        <span key={topping.group} className="proDetail d-block fst-italic">
                          {topping.group}: {topping.label} (${topping.price})
                        </span>
                      ))}
                      <div className="cartItemActionsRow">
                        <div className="cartItemActions">
                          <button
                            type="button"
                            className="cartCommentBtn"
                            onClick={() => {
                              setActiveCartId(half.cart_id);
                              setIsCommentModalOpen(true);
                            }}
                          >
                            <svg width="14" height="14">
                              <use xlinkHref="/images/sprite.svg#iconMessage" />
                            </svg>
                            Comment
                          </button>
                        </div>
                      </div>
                      {half.comments && (
                        <div className="cartItemCommentRow">
                          <svg width="14" height="14">
                            <use xlinkHref="/images/sprite.svg#iconMessage" />
                          </svg>
                          <span>{half.comments}</span>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            ))}

              {(() => {
                const renderCartItem = (item) => (
                  <div className="cartSidebarPannel" key={item.cart_id}>
                    <div className="mProTopRow">
                      <CartImage src={item.first_image_url} alt={item.item_name} />
                      <div className="mProName" onClick={() => onEditCartItem(item)}>
                        {item.item_name} - {item.selected_size}
                      </div>
                      <span className="priceLg">${item.total_price}</span>
                    </div>
                    <p>
                      {item.product_detail_text ? (
                        <span dangerouslySetInnerHTML={{ __html: item.product_detail_text }} />
                      ) : (
                        <span></span>
                      )}
                    </p>
                    <div className="cartItemActionsRow">
                      <div className="cartItemActions">
                        <button
                          type="button"
                          className="cartRemoveBtn"
                          onClick={() => handleRemoveCartItem(item, !!item.is_previously_added)}
                          disabled={removeID === item.cart_id}
                        >
                          <svg width="14" height="14">
                            <use xlinkHref="/images/sprite.svg#iconTrash"></use>
                          </svg>
                          Remove
                        </button>
                        <button
                          type="button"
                          className="cartCommentBtn"
                          onClick={() => {
                            setActiveCartId(item.cart_id);
                            setIsCommentModalOpen(true);
                          }}
                        >
                          <svg width="14" height="14">
                            <use xlinkHref="/images/sprite.svg#iconMessage" />
                          </svg>
                          Comment
                        </button>
                      </div>
                      {item.is_previously_added ? (
                        <button
                          type="button"
                          className="cartRepeatBtn"
                          onClick={() => handleRepeatItem(item)}
                          disabled={repeatingCartId === item.cart_id}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M17 2.1l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M3 12.9a9 9 0 0 1 15-6.7l3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 21.9l-4-4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M21 11.1a9 9 0 0 1-15 6.7l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {repeatingCartId === item.cart_id ? 'Repeating...' : 'Repeat'}
                        </button>
                      ) : (
                        <div className="quantitySelector">
                        <span className="qtyBtn minus"
                        onClick={() => handleUpdateQty(item, item.item_quantity - 1)}
                        >
                          <svg width="8" height="8">
                            <use xlinkHref="./images/sprite.svg#iconMinus"></use>
                          </svg>
                        </span>
                        <input
                          type="number"
                          className="form-control quantityInput"
                          value={item.item_quantity}
                          min="1"
                          readOnly
                          onClick={() => setQuantityEditItem(item)}
                          onFocus={(e) => e.target.blur()}
                          style={{ cursor: 'pointer', caretColor: 'transparent' }}
                        />
                        <span className="qtyBtn plus"
                        onClick={() => handleUpdateQty(item, item.item_quantity + 1)}
                        >
                          <svg width="8" height="8">
                            <use xlinkHref="./images/sprite.svg#iconPlus"></use>
                          </svg>
                        </span>
                      </div>
                      )}
                    </div>
                    {item.comments && (
                      <div className="cartItemCommentRow">
                        <svg width="14" height="14">
                          <use xlinkHref="/images/sprite.svg#iconMessage" />
                        </svg>
                        <span>{item.comments}</span>
                      </div>
                    )}
                  </div>
                );

                // Split on the data, not on editOrderId. is_previously_added is per item and
                // self-describing: an item carrying it has been on a kitchen docket, however this
                // cart was opened. Also gating on editOrderId meant the sections disappeared
                // whenever the existing-order lookup came back empty - no table_occupancy row, a
                // refresh straight onto /takeaway, or the async resolve not having landed yet -
                // even though the items themselves plainly said they had been sent.
                //
                // The brand-new-cart case the old guard was protecting is already covered below:
                // nothing is flagged, previousItems is empty, and it falls through to the same
                // flat list as before.
                // Most-recently-added item first, matching the order items were entered in.
                const previousItems = cartItems.filter((item) => item.is_previously_added);
                const newItems = cartItems.filter((item) => !item.is_previously_added).reverse();

                if (previousItems.length === 0) {
                  return cartItems.slice().reverse().map(renderCartItem);
                }

                return (
                  <div className="cartItemsSplit">
                    {newItems.length > 0 && (
                      <div className="cartNewItemsArea">
                        <div className="cartSectionHeader cartSectionHeader--new">Newly Added Items</div>
                        {newItems.map(renderCartItem)}
                      </div>
                    )}
                    <div className="cartPreviousItemsArea">
                      <div className="cartSectionHeader cartSectionHeader--previous">Previously Added Items</div>
                      <div className="cartPreviousItemsScroll">
                        {previousItems.map(renderCartItem)}
                      </div>
                    </div>
                  </div>
                );
              })()}
          </>
          )}
        </div>
      </div>

      <div className="cartSidebarFooter">
        {orderType === 'dinein' && (
          <div className="cartServiceTypeRow">
            <span className="cartServiceTypeLabel">Service Type</span>
            <div className="cartServiceTypeOptions">
              {(isPagerOrder
                ? [
                    { value: 'pager_takeaway', label: 'Takeaway' },
                    { value: 'pager_dine_in', label: 'Dine-in' },
                  ]
                : [
                    { value: 'entree_first', label: 'Entree First' },
                    { value: 'all_together', label: 'All Together' },
                    { value: 'running', label: 'Running' },
                    { value: 'ignore', label: 'Ignore' },
                  ]
              ).map((option) => (
                <label key={option.value} className="cartServiceTypeOption">
                  <input
                    type="radio"
                    name="cartServiceType"
                    value={option.value}
                    checked={serviceType === option.value}
                    onChange={(e) => setServiceType(e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="csFooterRow">
          <span className="csfLbl">Total Price for {cartItems.length} Items</span>
          <span className="priceVal">${netTotal}</span>
        </div>
        {(cartItems.length > 0 || dealsItem.length > 0 || halfAndHalfItems.length > 0)  && (
          <div className="csFooterRow">
            <button
              className={`btn btnLight smBtn ${!hasNewItems ? 'disabled' : ''}`}
              disabled={!hasNewItems}
              onClick={
                hasNewItems
                  ? () =>
                      navigate('/order-review', {
                        state: {
                          orderType: localStorage.getItem('orderType'),
                          selectedTables: getSelectedTableIds(),
                          existing_order_id: existingOrderId,
                          edit_order_id:editOrderId,
                          service_type: orderType === 'dinein' ? serviceType : undefined,
                          tabId
                        }
                      })
                  : undefined
              }
            >
              Order Review
            </button>
            {/* A pager is always prepaid at the counter, so this cart page's own "Send to
                Kitchen" shortcut (which would create the order without payment) is dropped for
                it, replaced by Quick Checkout below - straight to the payment page, with Order
                Review (-> PREPAID) still available above as the non-shortcut path. */}
            {orderType === 'dinein' && !isPagerOrder && (
              <a
                onClick={!isSendingToKitchen && hasNewItems ? handleQuickSendToKitchen : undefined}
                className={`btn btnPrimary smBtn ${isSendingToKitchen || !hasNewItems ? 'disabled' : ''}`}
              >
                {isSendingToKitchen ? 'Sending...' : 'Send to Kitchen'}
              </a>
            )}
            {isPagerOrder && (
              <a
                onClick={!isPagerCheckingOut && hasNewItems ? handlePagerQuickCheckout : undefined}
                className={`btn btnPrimary smBtn ${isPagerCheckingOut || !hasNewItems ? 'disabled' : ''}`}
              >
                {isPagerCheckingOut ? 'Processing...' : 'Quick Checkout'}
              </a>
            )}
            {isTakeaway &&  !editOrderId &&  (
              <a
                onClick={!isLoading ? handleQuickCheckout : undefined}
                className={`btn btnPrimary smBtn ${isLoading ? 'disabled' : ''}`}
              >
                {isLoading ? 'Processing...' : 'Quick Checkout'}
              </a>
            )}
          </div>
        )}

      <CartCommentModal
        isOpen={isCommentModalOpen}
        cartId={activeCartId}
        onClose={() => {
          setIsCommentModalOpen(false);
          setActiveCartId(null);
        }}
        onSuccess={() => {
          fetchCartWithDeals();
        }}
      />

      <QuantityKeypadModal
        isOpen={!!quantityEditItem}
        item={quantityEditItem}
        onClose={() => setQuantityEditItem(null)}
        onConfirm={handleUpdateQty}
      />

<CustomItemModal
    isOpen={isCustomItemModalOpen}
    onClose={()=>setIsCustomItemModalOpen(false)}
    onSave={handleAddCustomItem}
/>

      <SelectServerModal
        isOpen={isServerModalOpen}
        servers={modalServers}
        isSubmitting={serverModalIntent === 'checkout' ? isPagerCheckingOut : isSendingToKitchen}
        onClose={() => setIsServerModalOpen(false)}
        onConfirm={(serverId) =>
          serverModalIntent === 'checkout'
            ? submitPagerQuickCheckout(serverId)
            : submitSendToKitchen(serverId)
        }
        confirmLabel={serverModalIntent === 'checkout' ? 'Checkout' : 'Send to Kitchen'}
        submittingLabel={serverModalIntent === 'checkout' ? 'Processing...' : 'Sending...'}
      />


      </div>
    </div>
  );
};

export default TakeawayCart;